import { useState } from "react";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { HomeButton } from "../components/HomeButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const LIME = "#84CC16";
const GREEN = "#10B981";
const LIME_LIGHT = "#BEF264";

const CATEGORIES = [
  { id: "length", label: "Length", icon: "📏", units: [
    { id: "nm", label: "Nanometers", f: 1e-9 }, { id: "um", label: "Micrometers", f: 1e-6 },
    { id: "mm", label: "Millimeters", f: 0.001 }, { id: "cm", label: "Centimeters", f: 0.01 },
    { id: "m", label: "Meters", f: 1 }, { id: "km", label: "Kilometers", f: 1000 },
    { id: "in", label: "Inches", f: 0.0254 }, { id: "ft", label: "Feet", f: 0.3048 },
    { id: "yd", label: "Yards", f: 0.9144 }, { id: "mi", label: "Miles", f: 1609.344 },
    { id: "nmi", label: "Nautical Miles", f: 1852 },
    { id: "au", label: "Astron. Units", f: 1.496e11 }, { id: "ly", label: "Light Years", f: 9.461e15 },
  ]},
  { id: "area", label: "Area", icon: "🗺️", units: [
    { id: "mm2", label: "sq mm", f: 1e-6 }, { id: "cm2", label: "sq cm", f: 1e-4 },
    { id: "m2", label: "sq meters", f: 1 }, { id: "km2", label: "sq km", f: 1e6 },
    { id: "in2", label: "sq inches", f: 6.4516e-4 }, { id: "ft2", label: "sq feet", f: 0.092903 },
    { id: "yd2", label: "sq yards", f: 0.836127 }, { id: "mi2", label: "sq miles", f: 2.59e6 },
    { id: "ac", label: "Acres", f: 4046.86 }, { id: "ha", label: "Hectares", f: 10000 },
  ]},
  { id: "mass", label: "Mass", icon: "⚖️", units: [
    { id: "ug", label: "Micrograms", f: 1e-9 }, { id: "mg", label: "Milligrams", f: 1e-6 },
    { id: "g", label: "Grams", f: 0.001 }, { id: "kg", label: "Kilograms", f: 1 },
    { id: "t", label: "Metric Tons", f: 1000 },
    { id: "oz", label: "Ounces", f: 0.0283495 }, { id: "lb", label: "Pounds", f: 0.453592 },
    { id: "st", label: "Stone", f: 6.35029 }, { id: "amu", label: "Atomic Mass Units", f: 1.66054e-27 },
  ]},
  { id: "volume", label: "Volume", icon: "🧪", units: [
    { id: "ml", label: "Milliliters", f: 1e-6 }, { id: "l", label: "Liters", f: 0.001 },
    { id: "m3", label: "Cubic Meters", f: 1 }, { id: "cm3", label: "cc / cm³", f: 1e-6 },
    { id: "gal", label: "Gallons (US)", f: 0.00378541 }, { id: "qt", label: "Quarts", f: 9.4635e-4 },
    { id: "pt", label: "Pints", f: 4.7318e-4 }, { id: "cup", label: "Cups", f: 2.3659e-4 },
    { id: "floz", label: "Fluid Oz", f: 2.9574e-5 }, { id: "tbsp", label: "Tablespoons", f: 1.4787e-5 },
    { id: "tsp", label: "Teaspoons", f: 4.9289e-6 }, { id: "galuk", label: "Gallons (UK)", f: 0.00454609 },
  ]},
  { id: "temp", label: "Temp", icon: "🌡️", special: "temp", units: [
    { id: "c", label: "Celsius" }, { id: "f", label: "Fahrenheit" }, { id: "k", label: "Kelvin" },
  ]},
  { id: "time", label: "Time", icon: "⏱️", units: [
    { id: "ns", label: "Nanoseconds", f: 1e-9 }, { id: "us", label: "Microseconds", f: 1e-6 },
    { id: "ms", label: "Milliseconds", f: 0.001 }, { id: "s", label: "Seconds", f: 1 },
    { id: "min", label: "Minutes", f: 60 }, { id: "hr", label: "Hours", f: 3600 },
    { id: "day", label: "Days", f: 86400 }, { id: "wk", label: "Weeks", f: 604800 },
    { id: "mo", label: "Months (30d)", f: 2592000 }, { id: "yr", label: "Years (365d)", f: 31536000 },
  ]},
  { id: "speed", label: "Speed", icon: "🏎️", units: [
    { id: "ms", label: "m/s", f: 1 }, { id: "kmh", label: "km/h", f: 0.277778 },
    { id: "mph", label: "mph", f: 0.44704 }, { id: "fts", label: "ft/s", f: 0.3048 },
    { id: "kn", label: "Knots", f: 0.514444 },
    { id: "mach", label: "Mach", f: 343 }, { id: "c", label: "Speed of Light", f: 299792458 },
  ]},
  { id: "angle", label: "Angle", icon: "📐", units: [
    { id: "deg", label: "Degrees", f: 1 }, { id: "rad", label: "Radians", f: 57.2958 },
    { id: "grad", label: "Gradians", f: 0.9 }, { id: "turn", label: "Turns", f: 360 },
    { id: "arcmin", label: "Arcminutes", f: 1/60 }, { id: "arcsec", label: "Arcseconds", f: 1/3600 },
  ]},
  { id: "energy", label: "Energy", icon: "⚡", units: [
    { id: "j", label: "Joules", f: 1 }, { id: "kj", label: "Kilojoules", f: 1000 },
    { id: "cal", label: "Calories", f: 4.184 }, { id: "kcal", label: "Kilocalories", f: 4184 },
    { id: "wh", label: "Watt-hours", f: 3600 }, { id: "kwh", label: "kWh", f: 3.6e6 },
    { id: "ev", label: "Electron Volts", f: 1.602e-19 }, { id: "btu", label: "BTU", f: 1055.06 },
  ]},
  { id: "force", label: "Force", icon: "💪", units: [
    { id: "n", label: "Newtons", f: 1 }, { id: "kn2", label: "Kilonewtons", f: 1000 },
    { id: "dyn", label: "Dynes", f: 1e-5 },
    { id: "lbf", label: "Pound-force", f: 4.44822 }, { id: "kgf", label: "Kilogram-force", f: 9.80665 },
  ]},
  { id: "power", label: "Power", icon: "🔌", units: [
    { id: "w", label: "Watts", f: 1 }, { id: "kw", label: "Kilowatts", f: 1000 },
    { id: "mw2", label: "Megawatts", f: 1e6 }, { id: "hp", label: "Horsepower", f: 745.7 },
    { id: "btuh", label: "BTU/hour", f: 0.29307 }, { id: "ftlbs", label: "ft-lb/s", f: 1.35582 },
  ]},
  { id: "pressure", label: "Pressure", icon: "🔧", units: [
    { id: "pa", label: "Pascals", f: 1 }, { id: "kpa", label: "Kilopascals", f: 1000 },
    { id: "bar", label: "Bar", f: 100000 }, { id: "atm", label: "Atmospheres", f: 101325 },
    { id: "psi", label: "PSI", f: 6894.76 }, { id: "mmhg", label: "mmHg (Torr)", f: 133.322 },
  ]},
  { id: "frequency", label: "Frequency", icon: "〰️", units: [
    { id: "hz", label: "Hertz", f: 1 }, { id: "khz", label: "Kilohertz", f: 1000 },
    { id: "mhz", label: "Megahertz", f: 1e6 }, { id: "ghz", label: "Gigahertz", f: 1e9 },
    { id: "rpm", label: "RPM", f: 1/60 },
  ]},
  { id: "density", label: "Density", icon: "🧱", units: [
    { id: "kgm3", label: "kg/m³", f: 1 }, { id: "gcm3", label: "g/cm³", f: 1000 },
    { id: "gml", label: "g/mL", f: 1000 }, { id: "kgl", label: "kg/L", f: 1000 },
    { id: "lbft3", label: "lb/ft³", f: 16.0185 }, { id: "lbin3", label: "lb/in³", f: 27679.9 },
  ]},
  { id: "electric", label: "Electric", icon: "🔋", units: [
    { id: "c", label: "Coulombs", f: 1 }, { id: "ah", label: "Amp-hours", f: 3600 },
    { id: "mah", label: "Milliamp-hours", f: 3.6 }, { id: "e", label: "Elementary charges", f: 1.602e-19 },
  ]},
  { id: "data", label: "Data", icon: "💾", units: [
    { id: "bit", label: "Bits", f: 1 }, { id: "byte", label: "Bytes", f: 8 },
    { id: "kb", label: "Kilobytes", f: 8000 }, { id: "mb", label: "Megabytes", f: 8e6 },
    { id: "gb", label: "Gigabytes", f: 8e9 }, { id: "tb", label: "Terabytes", f: 8e12 },
    { id: "kib", label: "Kibibytes", f: 8192 }, { id: "mib", label: "Mebibytes", f: 8388608 },
    { id: "gib", label: "Gibibytes", f: 8589934592 },
  ]},
  { id: "fuel", label: "Fuel", icon: "⛽", special: "fuel", units: [
    { id: "mpg", label: "MPG (US)" }, { id: "mpguk", label: "MPG (UK)" },
    { id: "lp100", label: "L/100km" }, { id: "kml", label: "km/L" },
  ]},
  { id: "moles", label: "Moles", icon: "⚗️", special: "moles", units: [
    { id: "mol", label: "Moles" }, { id: "g2", label: "Grams" },
    { id: "mg2", label: "Milligrams" }, { id: "kg2", label: "Kilograms" },
    { id: "particles", label: "Particles" },
  ]},
];

function convertTemp(v, from, to) {
  const n = parseFloat(v); if (isNaN(n)) return "";
  let c = from === "c" ? n : from === "f" ? (n - 32) * 5/9 : n - 273.15;
  if (to === "c") return c; if (to === "f") return c * 9/5 + 32; return c + 273.15;
}

// Fuel: convert everything through L/100km as base
function convertFuel(v, from, to) {
  const n = parseFloat(v); if (isNaN(n) || n === 0) return "";
  // To L/100km
  let lp100;
  if (from === "lp100") lp100 = n;
  else if (from === "mpg") lp100 = 235.215 / n;
  else if (from === "mpguk") lp100 = 282.481 / n;
  else if (from === "kml") lp100 = 100 / n;
  // From L/100km
  if (to === "lp100") return lp100;
  if (to === "mpg") return 235.215 / lp100;
  if (to === "mpguk") return 282.481 / lp100;
  if (to === "kml") return 100 / lp100;
  return "";
}

const AVOGADRO = 6.02214076e23;
function convertMoles(v, from, to, mw) {
  const n = parseFloat(v); const m = parseFloat(mw);
  if (isNaN(n) || (from !== "particles" && to !== "particles" && isNaN(m))) return "";
  if (isNaN(m) && (from !== "particles" && to !== "particles")) return "";
  // Convert to moles first
  let moles;
  if (from === "mol") moles = n;
  else if (from === "g2") moles = n / m;
  else if (from === "mg2") moles = n / (m * 1000);
  else if (from === "kg2") moles = (n * 1000) / m;
  else if (from === "particles") moles = n / AVOGADRO;
  // Convert from moles
  if (to === "mol") return moles;
  if (to === "g2") return moles * m;
  if (to === "mg2") return moles * m * 1000;
  if (to === "kg2") return (moles * m) / 1000;
  if (to === "particles") return moles * AVOGADRO;
  return "";
}

function fmt(num) {
  if (num === "" || num === null || num === undefined) return "";
  const n = parseFloat(num); if (isNaN(n)) return "";
  if (n === 0) return "0";
  if (Math.abs(n) >= 1e10 || (Math.abs(n) < 1e-4 && n !== 0)) return n.toExponential(6);
  return formatNum(n, 8);
}

export default function UnitConverterApp() {
  const [catId, setCatId] = useState("length");
  const [fromUnit, setFromUnit] = useState("m");
  const [toUnit, setToUnit] = useState("ft");
  const [value, setValue] = useState("");
  const [molWeight, setMolWeight] = useState("");

  const cat = CATEGORIES.find(c => c.id === catId);

  const handleCatChange = (id) => {
    setCatId(id);
    const nc = CATEGORIES.find(c => c.id === id);
    setFromUnit(nc.units[0].id);
    setToUnit(nc.units[1]?.id || nc.units[0].id);
    setValue(""); setMolWeight("");
  };

  const swap = () => { setFromUnit(toUnit); setToUnit(fromUnit); };

  let result = "";
  if (value !== "" && !isNaN(parseFloat(value))) {
    if (cat.special === "temp") result = fmt(convertTemp(value, fromUnit, toUnit));
    else if (cat.special === "fuel") result = fmt(convertFuel(value, fromUnit, toUnit));
    else if (cat.special === "moles") result = fmt(convertMoles(value, fromUnit, toUnit, molWeight));
    else {
      const from = cat.units.find(u => u.id === fromUnit);
      const to = cat.units.find(u => u.id === toUnit);
      if (from && to) result = fmt((parseFloat(value) * from.f) / to.f);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: "-25%", right: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(LIME, 0.04)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(LIME, 0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor={LIME} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${LIME}, ${GREEN})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>U</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${LIME_LIGHT}, #6EE7B7)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>UnitConvert</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdStack</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 680, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 36, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-1px", background: `linear-gradient(135deg, #ECFCCB, ${LIME_LIGHT} 40%, #6EE7B7 70%, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Unit Converter
          </h1>
          <p style={{ fontSize: 14, color: "#6B7280", margin: 0 }}>18 categories. Length, mass, temperature, chemistry, and more.</p>
        </div>

        {/* Category grid — 6 columns */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 5, marginBottom: 24 }}>
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => handleCatChange(c.id)} style={{
              padding: "8px 4px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
              background: catId === c.id ? rgba(LIME, 0.1) : "rgba(255,255,255,0.02)",
              border: catId === c.id ? `1.5px solid ${rgba(LIME, 0.3)}` : "1.5px solid rgba(255,255,255,0.04)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
              transition: "all 0.2s ease",
            }}>
              <span style={{ fontSize: 14 }}>{c.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 600, color: catId === c.id ? LIME_LIGHT : "#6B7280" }}>{c.label}</span>
            </button>
          ))}
        </div>

        {/* Converter card */}
        <div style={{ padding: "24px 22px", borderRadius: 20, background: "rgba(10,11,20,0.6)", border: `1px solid ${rgba(LIME, 0.08)}`, boxShadow: "0 4px 30px rgba(0,0,0,0.2)" }}>

          {/* Moles: molecular weight input */}
          {cat.special === "moles" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6, display: "block" }}>Molecular Weight (g/mol)</label>
              <input type="number" value={molWeight} onChange={e => setMolWeight(e.target.value)} placeholder="e.g. 18.015 for water, 58.44 for NaCl"
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${rgba(LIME, 0.2)}`,
                  background: rgba(LIME, 0.04), color: LIME_LIGHT, fontSize: 15, fontWeight: 600,
                  fontFamily: "'DM Sans', system-ui, sans-serif", outline: "none", boxSizing: "border-box",
                }} />
              <div style={{ fontSize: 11, color: "#4B5563", marginTop: 6 }}>
                Common: H₂O = 18.015 · NaCl = 58.44 · CO₂ = 44.01 · C₆H₁₂O₆ = 180.16 · O₂ = 32.00
              </div>
            </div>
          )}

          {/* From */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6, display: "block" }}>From</label>
            <div style={{ display: "flex", gap: 10 }}>
              <input type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="Enter value"
                style={{
                  flex: 1, padding: "14px", borderRadius: 10, border: `1px solid ${rgba(LIME, 0.12)}`,
                  background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 18, fontWeight: 600,
                  fontFamily: "'DM Sans', system-ui, sans-serif", outline: "none", boxSizing: "border-box",
                }} />
              <select value={fromUnit} onChange={e => setFromUnit(e.target.value)} style={{
                flex: 1, padding: "14px", borderRadius: 10, border: `1px solid ${rgba(LIME, 0.12)}`,
                background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 14,
                fontFamily: "'DM Sans', system-ui, sans-serif", outline: "none", cursor: "pointer",
              }}>
                {cat.units.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </div>
          </div>

          {/* Swap */}
          <div style={{ display: "flex", justifyContent: "center", margin: "4px 0" }}>
            <button onClick={swap} style={{
              width: 40, height: 40, borderRadius: 20, border: `1px solid ${rgba(LIME, 0.15)}`,
              background: rgba(LIME, 0.05), color: LIME_LIGHT, cursor: "pointer",
              fontSize: 18, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
            }}>⇅</button>
          </div>

          {/* To */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6, display: "block" }}>To</label>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{
                flex: 1, padding: "14px", borderRadius: 10, border: `1px solid ${rgba(LIME, 0.12)}`,
                background: rgba(LIME, 0.03), color: LIME_LIGHT, fontSize: 18, fontWeight: 700,
                fontFamily: "'DM Sans', system-ui, sans-serif", minHeight: 50, display: "flex", alignItems: "center",
                wordBreak: "break-all",
              }}>
                {result || <span style={{ color: "#374151" }}>Result</span>}
              </div>
              <select value={toUnit} onChange={e => setToUnit(e.target.value)} style={{
                flex: 1, padding: "14px", borderRadius: 10, border: `1px solid ${rgba(LIME, 0.12)}`,
                background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 14,
                fontFamily: "'DM Sans', system-ui, sans-serif", outline: "none", cursor: "pointer",
              }}>
                {cat.units.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </div>
          </div>

          {/* Summary line */}
          {value && result && (
            <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10, background: rgba(LIME, 0.04), border: `1px solid ${rgba(LIME, 0.06)}`, fontSize: 13, color: "#6B7280", textAlign: "center" }}>
              {value} {cat.units.find(u => u.id === fromUnit)?.label} = <strong style={{ color: LIME_LIGHT }}>{result}</strong> {cat.units.find(u => u.id === toUnit)?.label}
            </div>
          )}
        </div>

        {/* Cross promo */}
        <div style={{ marginTop: 32, padding: "20px", borderRadius: 16, background: `linear-gradient(135deg, ${rgba(LIME, 0.06)}, ${rgba(GREEN, 0.06)})`, border: `1px solid ${rgba(LIME, 0.12)}`, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: LIME_LIGHT, marginBottom: 6 }}>More NerdStack tools</div>
          {/* Cross-promo (rotates per page load) */}
          <CrossPromo currentSlug="convert" accent={LIME} accent2={GREEN} accentLight={LIME_LIGHT} />
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(LIME, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
        input::placeholder { color: #374151; }
        select { color-scheme: dark; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
